import { debounce } from 'lodash';
import { Subscription, Observable } from 'rxjs';
import { mergeMap, filter, first, takeUntil } from 'rxjs/operators';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { BcRestApi } from 'common/api/rest-api';
import { WorkItemListService } from 'common/workitems/workitem-list.service';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { rcEventRoutes } from 'common/common/constants/remote-control';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { BrowserSupportService } from 'shared/services/browser-support/browser-support.service';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { WorkItemChat } from 'common/workitems/models/work-item-chat';
import { ChatPinInvite } from 'common/workitems/interfaces/chat-pin-invite.interface';
import { PinviteList } from 'common/workitems/lists/pinvite-list';
import { WorkItemChatPinInvite } from 'common/workitems/models/workitem-chat-pin-invite-model';
import { PinType } from 'common/workitems/constants/pin-types';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { PersonType } from 'common/common/constants/person-type.enum';
import { WorkItemEventParams, WorkItemEvents, WorkItemEventValues } from 'common/common/constants/analytics-enums';
import { Component, OnDestroy, OnInit, ChangeDetectorRef, NgZone, ViewEncapsulation, Input } from '@angular/core';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { AudioService } from 'common/audio-service/audio.service';
import { CoolLocalStorage } from '@angular-cool/storage';
import { Folder } from 'common/xislivechat/models/folder';
import { taskItemAnimation } from 'components/accordion/workitem-list/shared/animations/task-item.animation';
import { ChatMessagesService } from 'common/chat-messages/chat-messages.service';
import { UserContextService } from 'common/user-context/user-context.service';
import { ChatFolderService } from 'common/folders-service/chat-folder.service';
import { BcCannedMessagesRepo } from 'common/canned-messages/canned-messages-repo';
import { ChatMessage } from 'common/xislivechat/models/chat-message';
import { BcConfigService } from 'common/config/provider';
import { CacheHelperService } from 'shared/services/cache-helper/cache-helper.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { ChatMessageBase } from 'common/xislivechat/models/chat-message-base';
import { RemoteControlService } from 'shared/services/remote-control/remote-control.service';

@Component({
  selector: 'app-work-item-chat-list',
  templateUrl: './work-item-chat-list.component.html',
  styleUrls: ['./work-item-chat-list.component.scss'],
  animations: [taskItemAnimation],
  encapsulation: ViewEncapsulation.None
})
export class WorkItemChatListComponent implements OnInit, OnDestroy {
  @Input()
  isHybrid: boolean;
  private _updateWorkitemInitialMessagesLoadedDebounced;
  private _rcHistorySubscription = { insert: null, remove: null };
  private _chatsFirstLoadedPromise: Promise<any>;
  private _rcControlMessageSubscription: Subscription;
  private _chatDetailsSubscription: Subscription;
  private _indicateFirstMessageFromNewChat: boolean;
  private _visitorLanguageCode: string;

  public isUseAnswerNextEnabled: boolean;
  public isPinviteEnabled: boolean;
  public chatWorkItemList: ChatTaskItem[];
  public chatPinviteWorkItemList: WorkItemChatPinInvite[];
  public initialInsertIsDone;
  public insertWorkItemsSubscription;
  public updateWorkItemsSubscription;
  public removeWorkItemsSubscription;
  public insertWorkItemMessagesSubscription;
  public rcEventRoutesUrlSubscription;
  public tooltipActionItems = [];
  public workItemType = WorkItemTypes.CHAT;
  public isSkeletonLoadingOn = false;
  public isVideoFeatureEnabled = false;
  public isVendorRemoteControlEnabled = false;

  constructor(private _bcSettingsService: BcSettingsService,
              private _chatFolderService: ChatFolderService,
              private _workItemListService: WorkItemListService,
              private _config: BcConfigService,
              private _analytics: BcAnalytics,
              private _workItemListenerService: WorkItemListenerService,
              private _bcRestApi: BcRestApi,
              private _userContextService: UserContextService,
              private _cannedMessagesRepo: BcCannedMessagesRepo,
              private _chatMessages: ChatMessagesService,
              private _audioService: AudioService,
              private _userPermissionService: UserPermissionService,
              private _browserSupportService: BrowserSupportService,
              private _rxEmitter: RxEmitter,
              private _pinviteList: PinviteList,
              private _workItemStateHandler: WorkItemStateHandler,
              private _localStorageService: CoolLocalStorage,
              private _changeDetector: ChangeDetectorRef,
              private _ngZone: NgZone,
              private _cacheHelper: CacheHelperService,
              private _remoteControlService: RemoteControlService) {

    this._updateWorkitemInitialMessagesLoadedDebounced = debounce(this._updateWorkitemInitialMessagesLoaded, 1000, { leading: false });

    this.chatWorkItemList = this._workItemListService.getChatWorkItemList();
    this.chatPinviteWorkItemList = this._pinviteList.chatPinviteWorkItemList;
    this._workItemListService.chatPinviteWorkItemList = this._pinviteList.chatPinviteWorkItemList;
    this.initialInsertIsDone = false;

    this.insertWorkItemsSubscription = null;
    this.updateWorkItemsSubscription = null;
    this.removeWorkItemsSubscription = null;
    this.insertWorkItemMessagesSubscription = null;
    this.rcEventRoutesUrlSubscription = null;
    this._chatDetailsSubscription = null;
    this.isPinviteEnabled = this._isPinInviteButtonEnabled();
    this.isUseAnswerNextEnabled = this._userContextService.userContext.operatorContext.useAnswerNext;
    this._indicateFirstMessageFromNewChat = false;
  }

  async ngOnInit(): Promise<void> {
    this.isSkeletonLoadingOn = this._config.current.features.skeletonLoading;

    this._getChatFolders();
    this._createStoreSubscriptionForChats();
    this._createBoldChatRemoteControlSubscription();
    this._rcUrlSubscription();
    await this._getFeatureValues();
    this._setEnabledTooltipActions();
  }

  ngOnDestroy(): void {
    if (this.insertWorkItemsSubscription) {
      this.insertWorkItemsSubscription.unsubscribe();
      this.insertWorkItemsSubscription = null;
    }
    if (this.removeWorkItemsSubscription) {
      this.removeWorkItemsSubscription.unsubscribe();
      this.removeWorkItemsSubscription = null;
    }
    if (this.updateWorkItemsSubscription) {
      this.updateWorkItemsSubscription.unsubscribe();
      this.updateWorkItemsSubscription = null;
    }
    if (this.insertWorkItemMessagesSubscription) {
      this.insertWorkItemMessagesSubscription.unsubscribe();
      this.insertWorkItemMessagesSubscription = null;
    }
    if (this.rcEventRoutesUrlSubscription) {
      this.rcEventRoutesUrlSubscription.unsubscribe();
      this.rcEventRoutesUrlSubscription = null;
    }

    if (this._rcHistorySubscription.insert) {
      this._rcHistorySubscription.insert.unsubscribe();
      this._rcHistorySubscription.insert = null;
    }

    if (this._rcHistorySubscription.remove) {
      this._rcHistorySubscription.remove.unsubscribe();
      this._rcHistorySubscription.remove = null;
    }

    if (this._rcControlMessageSubscription) {
      this._rcControlMessageSubscription.unsubscribe();
      this._rcControlMessageSubscription = null;
    }

    if (this._chatDetailsSubscription) {
      this._chatDetailsSubscription.unsubscribe();
      this._chatDetailsSubscription = null;
    }

    this._workItemListService.resetChats();
  }

  acceptButtonClicked(workItem: WorkItemChat, event): Promise<void> {
    if (!workItem.IsAnswered) {
      this._analytics.eventTrack(WorkItemEvents.WORK_ITEM_ACCEPTED, {
        [WorkItemEventParams.FROM]: WorkItemEventValues.BUTTON,
        [WorkItemEventParams.TYPE]: WorkItemTypes.CHAT,
        [WorkItemEventParams.VISITOR_LANGUAGE]: this._visitorLanguageCode,
        [WorkItemEventParams.QUICK_ACCEPT]: true
      });

      return this._acceptWorkItem(workItem)
        .then(() => {
          return this._cannedMessagesRepo.getChatWelcomeCannedMessageFromChatId(workItem.id)
            .then((cannedMessage) => {
              if (!cannedMessage) {
                return Promise.resolve();
              }

              return this._chatMessages.sendMessage({
                ChatID: workItem.id,
                Name: this._userContextService.userContext.name,
                Text: cannedMessage.text
              });
            });
        });
    }

    event.stopPropagation();
    event.preventDefault();
  }

  generateLastMessagesClass(lastMessage): string {
    let classStr = 'message';
    if (ChatMessage.isAgent(lastMessage)) {
      classStr += ' sent';
    }
    if (ChatMessage.isVisitor(lastMessage)) {
      classStr += ' received';
    }
    return classStr;
  }

  selectWorkitem(workitem: WorkItemChat): void {
    this._workItemListService.selectAndAcceptChatItem(workitem);
  }

  isFocused(workItemId: string): boolean {
    return this._workItemStateHandler.isFocused(workItemId);
  }

  isOpened(workItem): boolean {
    return this._workItemStateHandler.isOpened(workItem.id);
  }

  selectPinviteItem(workItem: ChatPinInvite): void {
    this._workItemListService.selectWorkitem(workItem);
  }

  onWorkitemKeydown(workitem: WorkItemChat, event): void {
    if (event.which !== KeyboardKeyCodes.ENTER) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.shiftKey) {
      if (!workitem.IsAnswered) {
        this.acceptButtonClicked(workitem, event);
      }
    } else {
      this.selectWorkitem(workitem);
    }

    this._changeDetector.detectChanges();
  }

  onPinviteWorkItemKeydown(workItem: ChatPinInvite, event): void {
    if (event.which !== KeyboardKeyCodes.ENTER) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.selectPinviteItem(workItem);
  }

  generateWorkitemClass(workitem: WorkItemChat): string {
    return this._workItemListService.generateWorkitemClass(workitem);
  }

  getLiveSessionsCount(): number {
    return this._workItemListService.getChatWorkitemCount() + this._pinviteList.chatPinviteWorkItemList.length;
  }

  onNewPinviteClicked(): void {
    if (!this._isOnlyOneActionEnabled()) {
      return;
    }

    const onlyAvailableAction: PinType | undefined = this._getOnlyAvailableAction();

    if (onlyAvailableAction === undefined) {
      throw new Error('Please specify pinType!');
    }

    this._createChatPinvite(onlyAvailableAction);
  }

  isNewPinviteButtonDisabled(): boolean {
    return this._workItemListService.isLiveSessionActive() || !this._pinviteList.canCreatePinvite() ||
      (!this._isRemoteControlEnabled() && !this._isCoBrowseEnabled());
  }

  workItemTrackByFn(index, item): string {
    return item.id;
  }

  _createStoreSubscriptionForChats(): void {
    this._chatsFirstLoadedPromise = this._workItemListenerService.createPromiseForInitialLoadingDone();
    this._chatsFirstLoadedPromise.then(() => this._notifyInitialInsertIsDone());

    this.insertWorkItemsSubscription = this._workItemListenerService.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.CHAT)
      .subscribe((workItem: WorkItemChat) => this._onInsertChatWorkItem(workItem));

    this.removeWorkItemsSubscription = this._workItemListenerService.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.CHAT)
      .subscribe((workItem: WorkItemChat) => this._onRemoveChatWorkItem(workItem));

    this.updateWorkItemsSubscription = this._workItemListenerService.createAcdBasedObservableForUpdateByWorkItemType(WorkItemTypes.CHAT)
      .subscribe((workItem: WorkItemChat) => this._onUpdateChatWorkItem(workItem));

    this._observeMessagesForWorkItem();
    this._observeChatDetailsForWorkItem();
  }

  _rcUrlSubscription(): void {
    this.rcEventRoutesUrlSubscription = this._rxEmitter.subscribe(rcEventRoutes.getRcURL, this._updateRemoteControlRoute.bind(this));
  }

  _onInsertChatWorkItem(workItem: WorkItemChat): void {
    if (this.initialInsertIsDone) {
      this._indicateFirstMessageFromNewChat = true;
      this._audioService.playNewChatArrived();
    }
    this._setInitialChatWorkItem(workItem);

    if (workItem.PinvitationID) {
      const selectedWorkItem = this._workItemListService.getSelectedWorkitem();

      if (selectedWorkItem && selectedWorkItem.isChatPinInvite()) {
        this._workItemListService.selectAndAcceptChatItem(workItem);

        switch ((<WorkItemChatPinInvite> selectedWorkItem).pinType) {
          case PinType.COBROWSE: {
            workItem.startAssistedBrowsing();
            this._analytics.eventTrack('StartAssistedBrowsing', { from: 'Pinvite' });
            break;
          }
          case PinType.RC: {
            workItem.startRCSession();
            if (this.isVendorRemoteControlEnabled) {
              this._remoteControlService.requestRemoteControlSession(workItem.id);
            }
            this._analytics.eventTrack('StartRCSession', { from: 'Pinvite' });
            break;
          }
          default: {
            break;
          }
        }
      }

      const fakeWorkItem = this._pinviteList.removeChatPinviteFromListLocallyByInvitationId(workItem.PinvitationID);

      if (fakeWorkItem) {
        this._workItemListService.removeWorkItem(fakeWorkItem);
      }
    }
  }

  _onUpdateChatWorkItem(workItem: WorkItemChat): void {
    this._updateChatWorkitemState(workItem);
    this._updateParentUnansweredMessageCount();
  }

  _onRemoveChatWorkItem(workItem: WorkItemChat): void {
    this._removeChatWorkItem(workItem);
    this._updateParentUnansweredMessageCount();
  }

  _observeMessagesForWorkItem(): void {
    this.insertWorkItemMessagesSubscription = this._workItemListenerService.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.CHAT).pipe(
      mergeMap(workItem =>
        this._workItemListenerService.createObservableForWorkItemLastChatMessages(workItem).pipe(
          takeUntil(this._observeChatWorkItemRemoval(workItem))
        )
      )
    ).subscribe(workItemMessage => this._insertWorkItemMessage(workItemMessage));
  }

  _observeChatDetailsForWorkItem(): void {
    this._chatDetailsSubscription = this._workItemListenerService.observeActiveChatsForVideoSessionStatus().subscribe(chatItem => {
      const chatWorkItem = this._getChatWorkitemById(chatItem.ChatID);
      chatWorkItem.VideoSessionStatus = chatItem.VideoSessionStatus;
    });
  }

  _insertWorkItemMessage(workItemMessage): void {
    this._buildLastMessageObject(workItemMessage);
    this._updateUnansweredMessageCount(workItemMessage);
    this._updateParentUnansweredMessageCount();
    this._sendAudioAndPopupNotification(workItemMessage);
    this._updateWorkitemInitialMessagesLoadedDebounced(workItemMessage);
    this._setUserLanguageCode(workItemMessage);
    this._sortMessages(workItemMessage);
  }

  _observeChatWorkItemRemoval(observedWorkItem): Observable<ChatItem> {
    return this._workItemListenerService.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.CHAT).pipe(
      filter((workItem: ChatItem) => workItem.id === observedWorkItem.id),
      first()
    );
  }

  _updateWorkitemInitialMessagesLoaded(workItemMessages): void {
    const workitem = this._getChatWorkitemById(workItemMessages.ItemID);
    if (workitem && workItemMessages && workItemMessages.messages.length) {
      workitem.messageInitialLoadIsFinished = true;
      this._changeDetector.detectChanges();
    }
  }

  _sendAudioAndPopupNotification(workItemMessage): void {
    if (!this.initialInsertIsDone) {
      return;
    }

    const workitem: ChatItem = this._getChatWorkitemById(workItemMessage.ItemID);
    if (!workitem || !workitem.messageInitialLoadIsFinished && !this._indicateFirstMessageFromNewChat) {
      return;
    }

    this._indicateFirstMessageFromNewChat = false;
    const numberOfMessages = workItemMessage.messages.length;
    const lastMessage = workItemMessage.messages[numberOfMessages - 1];

    if (lastMessage.PersonType !== PersonType.Operator) {
      this._audioService.playNewChatMessageArrived();
    }
  }

  _sortMessages(workItemMessage): void {
    const workitem = this._getChatWorkitemById(workItemMessage.ItemID);
    workitem.messages.sort(WorkItemStateHandler.compareObjectsByCreatedDateAsc);
  }

  _notifyInitialInsertIsDone(): void {
    this.initialInsertIsDone = true;
    this._updateChatWorkItemsFolderNamesIfNecessary();
    this._updateParentUnansweredMessageCount();
  }

  _updateParentUnansweredMessageCount(): void {
    if (this.initialInsertIsDone) {
      this._workItemListService.updateParentUnansweredMessageCount();
    }
  }

  _getChatFolders(): Promise<Folder[]> {
    return this._chatFolderService.getFolders();
  }

  _setInitialChatWorkItem(workItem: WorkItemChat): void {
    const item = {
      NewItem: true,
      FolderName: '',
      IsAnswered: this._workItemListService.isAnswered(workItem),
      unansweredMessages: 0,
      secondsRemainingDisplay: '',
      showSecondsRemaining: false,
      messageInitialLoadIsFinished: false
    };

    workItem.decorateWithDefaultValues(item);

    this._updateChatFoldersIfApplicable(workItem, { FolderID: workItem.FolderID });
    this._updateChatDepartmentIfApplicable(workItem, { DepartmentID: workItem.DepartmentID });

    this._workItemListService.addTimerToItemIfApplicable(workItem);

    this._loadRemoteControlRoute(workItem);

    this.chatWorkItemList.unshift(workItem);

    this._sortChatWorkItems();

    this._changeDetector.detectChanges();
  }

  _loadRemoteControlRoute(workItem: WorkItemChat): void {
    workItem.remoteControlRoute = this._localStorageService.getItem(bcSessionKeys.storage.remoteControlRoute + workItem.id);
  }

  _sortChatWorkItems(): void {
    if (this.initialInsertIsDone) {
      this.chatWorkItemList.sort(this._workItemStateHandler.sortChatItems.bind(this._workItemStateHandler));
    }
  }

  _updateChatWorkitemState(changes): void {
    const workitem = this._getChatWorkitemById(changes.id);

    this._workItemListService.stopTimerForItem(workitem);
    this._workItemListService.updateWorkItem(workitem, changes);
    this._workItemListService.addTimerToItemIfApplicable(workitem);

    this._updateChatFoldersIfApplicable(workitem, changes);
    this._updateChatDepartmentIfApplicable(workitem, changes);
  }

  _updateChatFoldersIfApplicable(workitem: ChatItem, changes): void {
    if (workitem && changes && changes.FolderID) {
      this._chatFolderService.getFolderName(changes.FolderID).then(folderName => {
        workitem.FolderName = folderName;
        workitem.departmentFolder = this._getDepartmentFolder(workitem);
      });
    } else if (workitem) {
      workitem.departmentFolder = this._getDepartmentFolder(workitem);
    }
  }

  _updateChatDepartmentIfApplicable(workitem: ChatItem, changes): void {
    if (workitem && changes && changes.DepartmentID) {
      const context = this;
      this._cacheHelper.getCachedDepartmentName(changes.DepartmentID).toPromise().then((depName) => {
        workitem.DepartmentName = depName;
        workitem.departmentFolder = context._getDepartmentFolder(workitem);
      });
    } else if (workitem) {
      workitem.departmentFolder = this._getDepartmentFolder(workitem);
    }
  }

  _getDepartmentFolder(workitem: ChatTaskItem): string {
    const departmentFolder = [];
    if (workitem) {
      if (!workitem.Answered) {
        if (workitem.FolderName) {
          departmentFolder.push(workitem.FolderName);
        }
        if (workitem.DepartmentName) {
          departmentFolder.push(workitem.DepartmentName);
        }
      }
    }
    return departmentFolder.join(', ');
  }

  _getChatWorkitemById(workitemId): ChatTaskItem {
    return this.chatWorkItemList.find(item => item.ItemID === workitemId);
  }

  _getChatWorkItemByChatId(chatId): ChatTaskItem {
    return this.chatWorkItemList.find(item => item.id === chatId.toString());
  }

  _updateChatWorkItemsFolderNamesIfNecessary() {
    this.chatWorkItemList.forEach((chatWorkItem: WorkItemChat) => {
      if (!chatWorkItem.FolderName || chatWorkItem.FolderName.length === 0) {
        this._chatFolderService.getFolderName(chatWorkItem.FolderID)
          .then(folderName => {
            chatWorkItem.FolderName = folderName;
          });
      }
    });
  }

  _htmlToPlaintext(text): string {
    const strippedText = text && text.replace ? text.replace(/<[^>]+>/gm, '') : '';
    if (strippedText.length > this._config.current.lastMessagesTextLength) {
      return strippedText.substring(0, this._config.current.lastMessagesTextLength);
    }

    return strippedText;
  }

  _buildLastMessageObject(workitemMessage): void {
    const workitem = this._getChatWorkitemById(workitemMessage.ItemID);
    if (workitem) {
      workitem.messages = workitemMessage.messages.map(message => {
        message.shortText = this._htmlToPlaintext(message.Text);
        return message;
      });
    }
  }

  _updateUnansweredMessageCount(workitemMessage): void {
    const numberOfMessages = workitemMessage.messages.length;
    const lastMessage = workitemMessage.messages[numberOfMessages - 1];
    const chatWorkItem = this._getChatWorkitemById(workitemMessage.ItemID);

    if (!chatWorkItem) {
      return;
    }

    if (ChatMessage.isAgent(lastMessage)) {
      chatWorkItem.unansweredMessages = 0;
    } else if (ChatMessage.isVisitor(lastMessage) && numberOfMessages >= chatWorkItem.unansweredMessages) {
      chatWorkItem.unansweredMessages++;
    }
  }

  _removeChatWorkItem(workItem: ChatItem): void {
    const chat = this._getChatWorkitemById(workItem.id);
    if (chat) {
      this._workItemListService.stopTimerForItem(chat);

      this.chatWorkItemList.splice(this.chatWorkItemList.indexOf(chat), 1);
    }

    this._workItemListService.removeWorkItem(workItem);
  }

  _acceptWorkItem(workitem: ChatItem): Promise<void> {
    workitem.IsAnswered = true; // hide quick accept button immediately
    return this._bcRestApi.answerChat(workitem.id)
      .then(() => {
        this._analytics.eventTrack('WorkItemAnswered', { type: WorkItemTypes.CHAT });
        this._workItemListService.stopTimerForItem(workitem);
      });
  }

  isWorkItemAcceptable(workItem: ChatItem): boolean {
    return workItem.isChat() && !workItem.IsAnswered;
  }

  private _createBoldChatRemoteControlSubscription(): void {
    this._chatsFirstLoadedPromise.then(() => {
      this._rcHistorySubscription.insert = this._workItemListenerService.createActiveRcHistoryItemsByLoginIdSubscriptions()
        .insert
        .subscribe(this._addRcHistoryDataToChat.bind(this));

      this._rcHistorySubscription.remove = this._workItemListenerService.createActiveRcHistoryItemsByLoginIdSubscriptions()
        .deleted
        .subscribe(this._removeRcHistoryDataFromChat.bind(this));
    });
  }

  private _addRcHistoryDataToChat(historyData): void {
    const workItem = this._getChatWorkItemByChatId(historyData.ChatID);

    if (workItem) {
      workItem.startRCSession();
      workItem.remoteControlHistoryId = historyData.RCHistoryID;
    }
  }

  private _removeRcHistoryDataFromChat(historyData): void {
    const workItem = this._getChatWorkItemByChatId(historyData.ChatID);

    if (workItem && historyData.RCHistoryID === workItem.remoteControlHistoryId) {
      workItem.clearHistoryItem();
    }
    this._localStorageService.removeItem(bcSessionKeys.storage.remoteControlRoute + workItem.id);
  }

  private _updateRemoteControlRoute(request): void {
    const selectedId = this._workItemListService.getSelectedWorkitemId();
    const workItem: ChatItem = this._getChatWorkItemByChatId(selectedId);
    workItem.remoteControlRoute = request.newUrl;
    this._localStorageService.setItem(bcSessionKeys.storage.remoteControlRoute + workItem.id, request.newUrl);
  }

  selectTooltipAction(action: string): void {
    this._createChatPinvite(PinType[action]);
  }

  private _createChatPinvite(pinType: PinType): void {
    this._pinviteList.createChatPinvite(pinType)
      .then((newPinviteWorkItem: WorkItemChatPinInvite) => {
        this._ngZone.run(() => {
          this._workItemListService.selectWorkitem(newPinviteWorkItem);
        });
      });
  }

  private _getOnlyAvailableAction(): PinType | undefined {
    if (this._isOnlyOneActionEnabled() && this.tooltipActionItems[0]) {
      return this.tooltipActionItems[0].type;
    }

    return;
  }

  private _isOnlyOneActionEnabled(): boolean {
    return this.tooltipActionItems.length === 1;
  }

  private _setEnabledTooltipActions(): void {
    if (this._isRemoteControlEnabled()) {
      this.tooltipActionItems.push(
        {
          action: PinType[PinType.RC],
          icon: 'remote-access-inactive',
          text: 'bcWorkitemChatList.startRemoteAccessWithPin',
          type: PinType.RC
        }
      );
    }

    if (this._isCoBrowseEnabled()) {
      this.tooltipActionItems.push(
        {
          action: PinType[PinType.COBROWSE],
          icon: 'assisted-browsing-inactive',
          text: 'bcWorkitemChatList.startCoBrowseWithPin',
          type: PinType.COBROWSE
        }
      );
    }
  }

  private async _getFeatureValues() {
    const settings = await this._bcSettingsService.getSettings();

    this.isVideoFeatureEnabled = this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_VIDEO_CHAT)
      && this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_NEW_VIDEO_CHAT);

    this.isVendorRemoteControlEnabled = this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.RC_NEW_REMOTE_CONTROL);
  }

  private _isPinInviteButtonEnabled(): boolean {
    return this._userPermissionService.hasPermissionFor(ActionPermissionType.PINVITE_ENABLED) &&
      (this._isRemoteControlEnabled() || this._isCoBrowseEnabled());
  }

  private _isRemoteControlEnabled(): boolean {
    return this._userPermissionService.hasPermissionFor(ActionPermissionType.RC_ENABLED) && (this._browserSupportService.isCmsLibSupportedByCurrentBrowser() || this.isVendorRemoteControlEnabled);
  }

  private _isCoBrowseEnabled(): boolean {
    return this._config && this._config.isFeatureSwitchOn('assistedBrowsing') &&
      this._browserSupportService.isAssistedBrowsingSupportedByCurrentBrowser() &&
      this._userPermissionService.hasPermissionForActionTypes([ActionPermissionType.ASSISTED_BROWSING_ENABLED, ActionPermissionType.ASSISTED_BROWSING_ACTION]);
  }

  private _setUserLanguageCode(workItemMessage): void {
    const messages = workItemMessage.messages.slice().reverse();
    const lastUserMessage = messages.find(message => message.PersonType === PersonType.Visitor);

    if (lastUserMessage) {
      this._visitorLanguageCode = lastUserMessage.LanguageCode;
    }
  }
}

interface ChatTaskItem extends ChatItem {
  messages?: ChatMessageBase[];
  unansweredMessages?: number;
}
